import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import {
    CalendarDays,
    FileX,
    Gift,
    ShieldCheck,
    Star,
    Timer,
    UserRound
} from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/kit/button'
import { FinalCta } from '@/components/kit/final-cta'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { FaqSection } from '@/components/home/home-faq'
import { FeatureTable } from '@/components/pricing/feature-table'
import { BillingToggle, PlanGrid } from '@/components/pricing/plans'
import { everyPlan, pricingFaqs } from '@/constants/pricing'
import { shortQuotes } from '@/constants/testimonials'

const icons = {
    gift: Gift,
    file: FileX,
    user: UserRound,
    calendar: CalendarDays,
    shield: ShieldCheck,
    timer: Timer
}

const Pricing: FC = (): ReactNode => {
    const [yearly, setYearly] = useState(false)

    return (
        <Layout>
            <Seo
                title='Pricing'
                description='Three plans from Updraft, the social media studio: Starter, Growth and Scale. Monthly or yearly, and every one starts with a free forecast.'
            />

            <section
                aria-labelledby='pricing-title'
                className='relative overflow-hidden pb-14 pt-36 text-center md:pt-44'
            >
                <div
                    aria-hidden='true'
                    className='bg-grid mask-radial absolute inset-0'
                />

                <div className='relative mx-auto flex max-w-[1100px] flex-col items-center px-5'>
                    <SectionLabel>Pricing · no small print</SectionLabel>

                    <SplitText
                        as='h1'
                        id='pricing-title'
                        lines={['Pick your', 'altitude']}
                        italic='altitude'
                        className='mt-6 text-[clamp(3.25rem,9.5vw,8.5rem)] font-bold leading-[0.86] tracking-[-0.045em]'
                    />

                    <Reveal
                        as='p'
                        delay={300}
                        className='mt-8 max-w-xl text-lg leading-relaxed text-stone md:text-xl'
                    >
                        Three plans, honest prices and a free forecast before you spend anything. Flip the switch to see what paying yearly saves you.
                    </Reveal>

                    <Reveal
                        delay={420}
                        className='mt-10'
                    >
                        <BillingToggle
                            yearly={yearly}
                            onChange={setYearly}
                        />
                    </Reveal>
                </div>
            </section>

            <section
                aria-label='Plans'
                className='mx-auto max-w-[1400px] px-5 pt-6 md:px-10'
            >
                <PlanGrid yearly={yearly} />

                <Reveal
                    delay={100}
                    className='mt-6'
                >
                    <div className='grain grain-light flex flex-col gap-8 rounded-[2rem] bg-ink p-7 text-cream [--grain-opacity:0.25] md:flex-row md:items-center md:justify-between md:p-10'>
                        <div className='max-w-2xl'>
                            <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-cream/60'>Bigger skies</p>
                            <h2 className='mt-3 text-[clamp(1.9rem,3.4vw,2.75rem)] font-bold leading-[1] tracking-[-0.035em]'>
                                Several brands, several countries, one very full calendar?
                            </h2>
                            <p className='mt-3 text-base leading-relaxed text-cream/70 md:text-lg'>
                                We build a custom crew around your team and your sign-off process, with a dedicated pod, local creators and reporting your board will actually read.
                            </p>
                        </div>
                        <Button
                            href='/contact?plan=enterprise'
                            size='lg'
                        >
                            Start a conversation
                        </Button>
                    </div>
                </Reveal>
            </section>

            <section
                aria-labelledby='every-plan-title'
                className='mx-auto max-w-[1400px] px-5 pt-20 md:px-10 md:pt-28'
            >
                <div className='flex flex-col gap-4 md:flex-row md:items-end md:justify-between'>
                    <div>
                        <SectionLabel>In every plan</SectionLabel>
                        <h2
                            id='every-plan-title'
                            className='mt-5 text-[clamp(2.25rem,4.6vw,4rem)] font-bold leading-[0.95] tracking-[-0.035em]'
                        >
                            The bits you get, whichever you pick
                        </h2>
                    </div>
                </div>

                <ul className='mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'>
                    {everyPlan.map((item, index) => {
                        const Icon = icons[item.icon]

                        return (
                            <Reveal
                                key={item.title}
                                as='li'
                                delay={index * 70}
                            >
                                <div className='flex h-full flex-col gap-5 rounded-[1.5rem] border border-black/5 bg-paper p-6'>
                                    <Icon
                                        aria-hidden='true'
                                        strokeWidth={1.3}
                                        className='size-8 text-ink'
                                    />
                                    <div>
                                        <h3 className='text-lg font-bold tracking-[-0.03em]'>{item.title}</h3>
                                        <p className='mt-1.5 text-sm leading-relaxed text-stone'>{item.body}</p>
                                    </div>
                                </div>
                            </Reveal>
                        )
                    })}
                </ul>
            </section>

            <FeatureTable />

            <section
                aria-label='Overheard from clients'
                className='bg-sand py-16 md:py-20'
            >
                <div className='mx-auto grid max-w-[1400px] gap-4 px-5 md:grid-cols-3 md:px-10'>
                    {shortQuotes.slice(0, 3).map((item, index) => (
                        <Reveal
                            key={item.name}
                            delay={index * 100}
                        >
                            <figure className='flex h-full flex-col justify-between rounded-[1.5rem] bg-paper p-7'>
                                <div className='flex text-ember'>
                                    {Array.from({ length: 5 }).map((_, star) => (
                                        <Star
                                            key={star}
                                            aria-hidden='true'
                                            className='size-4 fill-current'
                                        />
                                    ))}
                                </div>
                                <blockquote className='mt-5 font-heading text-2xl font-bold leading-[1.15] tracking-[-0.03em]'>
                                    “{item.quote}”
                                </blockquote>
                                <figcaption className='mt-6 text-sm'>
                                    <span className='font-semibold'>{item.name}</span>
                                    <span className='text-stone'>, {item.company}</span>
                                </figcaption>
                            </figure>
                        </Reveal>
                    ))}
                </div>
            </section>

            <FaqSection
                items={pricingFaqs}
                id='pricing-faq'
                lines={['Money', 'questions']}
            />

            <FinalCta
                lines={['Start with a', 'free forecast']}
                italic='free'
                body='One page on where your category is heading, three ideas built for it and our honest take on which plan you need. No card required.'
            />
        </Layout>
    )
}

export default Pricing
